import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  GfFlyerItemCategory,
  RecommendationEnginePreviewDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../../utils/api";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { useFlyerItemCategories } from "../../../../hooks/useFlyerItemCategories";
import {
  RecommendationEngineEndpoint,
  RecommendationEngineEndpointConfig,
} from "../../../../types/recommendationEngine";
import { CustomAutocompleteMultiSelect } from "../../../components/CustomAutocomplete";
import InfoTooltip from "./InfoTooltip";
import PreviewResultsTable, {
  PreviewItem,
  toPreviewItems,
} from "./PreviewResultsTable";
import { PREVIEW_FIELD_TOOLTIPS } from "../copy";

type Props = {
  open: boolean;
  endpoint: RecommendationEngineEndpoint;
  pipeline: RecommendationEngineEndpointConfig;
  onClose: () => void;
};

const toPreviewEndpointEnum = (endpoint: RecommendationEngineEndpoint) => {
  switch (endpoint) {
    case "similar":
      return RecommendationEnginePreviewDto.EndpointEnum.Similar;
    case "recommended":
      return RecommendationEnginePreviewDto.EndpointEnum.Recommended;
    case "nearby_picks":
      return RecommendationEnginePreviewDto.EndpointEnum.NearbyPicks;
    case "trending_now":
      return RecommendationEnginePreviewDto.EndpointEnum.TrendingNow;
    case "browse_by_interest":
      return RecommendationEnginePreviewDto.EndpointEnum.BrowseByInterest;
    case "smart_picks":
      return RecommendationEnginePreviewDto.EndpointEnum.SmartPicks;
    default:
      return RecommendationEnginePreviewDto.EndpointEnum.Recommended;
  }
};

const parseIdList = (value: string): string[] | undefined => {
  const list = value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
};

const parseOptionalNumber = (value: string): number | undefined => {
  if (!value.trim()) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const PreviewModal = ({ open, endpoint, pipeline, onClose }: Props) => {
  const authHeaders = useAuthHeaders();
  const { flyerItemCategories } = useFlyerItemCategories();
  const [sourceFlyerItemId, setSourceFlyerItemId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<
    GfFlyerItemCategory[]
  >([]);
  const [promotionTagIds, setPromotionTagIds] = useState("");
  const [favoriteStoreIds, setFavoriteStoreIds] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [radiusKm, setRadiusKm] = useState("");
  const [hoursWindow, setHoursWindow] = useState("");
  const [limit, setLimit] = useState(5);
  const [items, setItems] = useState<PreviewItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handlePreview = async () => {
    if (
      (endpoint === "similar" || endpoint === "recommended") &&
      !sourceFlyerItemId.trim()
    ) {
      setError("Source flyer item ID is required for this endpoint.");
      return;
    }

    if (
      endpoint === "nearby_picks" &&
      (!latitude.trim() || !longitude.trim())
    ) {
      setError("Latitude and longitude are required for Nearby Picks preview.");
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const selectedCategoryIds = selectedCategories
        .map((category) => category.id)
        .filter((id): id is string => Boolean(id));
      const body = {
        endpoint: toPreviewEndpointEnum(endpoint),
        sourceFlyerItemId: sourceFlyerItemId.trim() || undefined,
        customerId: customerId.trim() || undefined,
        categoryIds:
          selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
        promotionTagIds: parseIdList(promotionTagIds),
        favoriteStoreIds: parseIdList(favoriteStoreIds),
        lat: parseOptionalNumber(latitude),
        lng: parseOptionalNumber(longitude),
        radiusKm: parseOptionalNumber(radiusKm),
        hoursWindow: parseOptionalNumber(hoursWindow),
        pipeline,
        limit,
      } as RecommendationEnginePreviewDto;
      const response =
        await adminApiService.recommendationEngineAdminControllerPreview(
          body,
          authHeaders,
        );
      setItems(toPreviewItems(response.items));
    } catch {
      setError(
        "Preview request failed. Check the context inputs and recommendation engine configuration.",
      );
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Preview recommendation engine configuration</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Preview runs against the current editor draft, including unsaved
            changes. It does not affect live ranking.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            Each row shows the final score (after post-processing) and the
            matched sources that surfaced the item before scoring. Expand the
            Signals row to inspect every measurement the formula used.
          </Typography>

          <Grid container spacing={2}>
            {(endpoint === "similar" || endpoint === "recommended") && (
              <Grid item xs={12}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Source flyer item ID"
                    value={sourceFlyerItemId}
                    onChange={(e) => setSourceFlyerItemId(e.target.value)}
                    placeholder="UUID of source flyer item"
                  />
                  <InfoTooltip
                    title={PREVIEW_FIELD_TOOLTIPS.sourceFlyerItemId}
                  />
                </Box>
              </Grid>
            )}

            {(endpoint === "nearby_picks" ||
              endpoint === "trending_now" ||
              endpoint === "smart_picks") && (
              <>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Latitude"
                      value={latitude}
                      onChange={(e) => setLatitude(e.target.value)}
                    />
                    <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.latitude} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Longitude"
                      value={longitude}
                      onChange={(e) => setLongitude(e.target.value)}
                    />
                    <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.longitude} />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Radius (km)"
                      value={radiusKm}
                      onChange={(e) => setRadiusKm(e.target.value)}
                    />
                    <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.radiusKm} />
                  </Box>
                </Grid>
              </>
            )}

            {(endpoint === "trending_now" || endpoint === "smart_picks") && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Hours window"
                    value={hoursWindow}
                    onChange={(e) => setHoursWindow(e.target.value)}
                    placeholder="Optional"
                  />
                  <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.hoursWindow} />
                </Box>
              </Grid>
            )}

            {(endpoint === "browse_by_interest" ||
              endpoint === "nearby_picks" ||
              endpoint === "trending_now" ||
              endpoint === "smart_picks") && (
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer ID"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="Optional customer context"
                  />
                  <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.customerId} />
                </Box>
              </Grid>
            )}

            {(endpoint === "browse_by_interest" ||
              endpoint === "smart_picks") && (
              <>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomAutocompleteMultiSelect<GfFlyerItemCategory>
                        title="Categories"
                        allowSelectAll
                        multiSelect
                        optionsList={flyerItemCategories.map((category) => ({
                          value: category,
                          label: category.name,
                        }))}
                        selectedValues={selectedCategories.map((category) => ({
                          value: category,
                          label: category.name,
                        }))}
                        handleOnChange={(value) =>
                          setSelectedCategories(value ?? [])
                        }
                        dataTestId="recommendation-engine-preview-categories"
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        Optional manual interest category override.
                      </Typography>
                    </Box>
                    <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.categoryIds} />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Promotion tag IDs"
                      value={promotionTagIds}
                      onChange={(e) => setPromotionTagIds(e.target.value)}
                      placeholder="Comma-separated UUIDs"
                      helperText="Optional manual interest promotion-tag override."
                    />
                    <InfoTooltip
                      title={PREVIEW_FIELD_TOOLTIPS.promotionTagIds}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Favorite store IDs"
                      value={favoriteStoreIds}
                      onChange={(e) => setFavoriteStoreIds(e.target.value)}
                      placeholder="Comma-separated UUIDs"
                      helperText="Optional manual favorite-store override."
                    />
                    <InfoTooltip
                      title={PREVIEW_FIELD_TOOLTIPS.favoriteStoreIds}
                    />
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={4}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Limit"
                  value={limit}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setLimit(
                      Number.isFinite(next)
                        ? Math.max(1, Math.min(20, next))
                        : 5,
                    );
                  }}
                />
                <InfoTooltip title={PREVIEW_FIELD_TOOLTIPS.limit} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePreview}
              disabled={isLoading}
              sx={{ minWidth: 100 }}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Run"
              )}
            </Button>
          </Box>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {items.length > 0 && <PreviewResultsTable items={items} />}
        {!isLoading && !error && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Run preview against the current recommendation engine editor draft.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewModal;
